import { useCallback, useEffect, useMemo, useState } from 'react';

import { UserFeatureToggles } from 'clients/UserClient/types';
import { useUpdateCurrentUserFeatureTogglesMutation } from 'hooks/shared/useUpdateCurrentUserFeatureTogglesMutation';
import { useCurrentUserQuery } from 'shared/legacy/hooks/useCurrentUserQuery';

export const useUserFeatureToggles = () => {
  const [dontShowMessagePreview, setDontShowMessagePreview] = useState<boolean | null>(null);
  const [dontShowWhatsAppMessagePreview, setDontShowWhatsAppMessagePreview] = useState<boolean | null>(null);
  const [dontShowQuestionnairePreview, setDontShowQuestionnairePreview] = useState<boolean | null>(null);

  const updateUserFeatureTogglesMutation = useUpdateCurrentUserFeatureTogglesMutation();
  const { data: currentUser, isLoading: isLoadingCurrentUser } = useCurrentUserQuery();

  const profileDontShowGeneratedMessage = useMemo<boolean | undefined>(() => {
    return (
      isLoadingCurrentUser ||
      currentUser?.features?.find((f) => f.name === UserFeatureToggles.RJ_COMMUNICATION_HUB_DONT_SHOW_GENERATED_MESSAGE)
        ?.enabled
    );
  }, [currentUser, isLoadingCurrentUser]);

  const profileDontShowWhatsappMessage = useMemo<boolean | undefined>(() => {
    return (
      isLoadingCurrentUser ||
      currentUser?.features?.find((f) => f.name === UserFeatureToggles.RJ_COMMUNICATION_HUB_DONT_SHOW_WHATSAPP_MESSAGE)
        ?.enabled
    );
  }, [currentUser, isLoadingCurrentUser]);

  const profileDontShowQuestionnairePreview = useMemo<boolean | undefined>(() => {
    return (
      isLoadingCurrentUser ||
      currentUser?.features?.find(
        (f) => f.name === UserFeatureToggles.RJ_COMMUNICATION_HUB_DONT_SHOW_QUESTIONNAIRE_PREVIEW,
      )?.enabled
    );
  }, [currentUser, isLoadingCurrentUser]);

  const updateFeatureToggles = useCallback(() => {
    const togglesToUpdate = [];

    if (dontShowMessagePreview !== null && dontShowMessagePreview !== profileDontShowGeneratedMessage) {
      togglesToUpdate.push({
        name: UserFeatureToggles.RJ_COMMUNICATION_HUB_DONT_SHOW_GENERATED_MESSAGE,
        enabled: dontShowMessagePreview,
      });
    }

    if (dontShowWhatsAppMessagePreview !== null && dontShowWhatsAppMessagePreview !== profileDontShowWhatsappMessage) {
      togglesToUpdate.push({
        name: UserFeatureToggles.RJ_COMMUNICATION_HUB_DONT_SHOW_WHATSAPP_MESSAGE,
        enabled: dontShowWhatsAppMessagePreview,
      });
    }

    if (dontShowQuestionnairePreview !== null && dontShowQuestionnairePreview !== profileDontShowQuestionnairePreview) {
      togglesToUpdate.push({
        name: UserFeatureToggles.RJ_COMMUNICATION_HUB_DONT_SHOW_QUESTIONNAIRE_PREVIEW,
        enabled: dontShowQuestionnairePreview,
      });
    }

    if (togglesToUpdate.length > 0) {
      updateUserFeatureTogglesMutation.mutate(togglesToUpdate);
    }
  }, [
    dontShowMessagePreview,
    profileDontShowGeneratedMessage,
    dontShowWhatsAppMessagePreview,
    profileDontShowWhatsappMessage,
    dontShowQuestionnairePreview,
    profileDontShowQuestionnairePreview,
    updateUserFeatureTogglesMutation,
  ]);

  useEffect(() => {
    if (!isLoadingCurrentUser && dontShowMessagePreview === null) {
      setDontShowMessagePreview(
        currentUser?.features?.find(
          (f) => f.name === UserFeatureToggles.RJ_COMMUNICATION_HUB_DONT_SHOW_GENERATED_MESSAGE,
        )?.enabled ?? false,
      );
    }
  }, [currentUser, isLoadingCurrentUser, dontShowMessagePreview]);

  useEffect(() => {
    if (!isLoadingCurrentUser && dontShowWhatsAppMessagePreview === null) {
      setDontShowWhatsAppMessagePreview(
        currentUser?.features?.find(
          (f) => f.name === UserFeatureToggles.RJ_COMMUNICATION_HUB_DONT_SHOW_WHATSAPP_MESSAGE,
        )?.enabled ?? false,
      );
    }
  }, [currentUser, isLoadingCurrentUser, dontShowWhatsAppMessagePreview]);

  useEffect(() => {
    if (!isLoadingCurrentUser && dontShowQuestionnairePreview === null) {
      setDontShowQuestionnairePreview(
        currentUser?.features?.find(
          (f) => f.name === UserFeatureToggles.RJ_COMMUNICATION_HUB_DONT_SHOW_QUESTIONNAIRE_PREVIEW,
        )?.enabled ?? false,
      );
    }
  }, [currentUser, isLoadingCurrentUser, dontShowQuestionnairePreview]);

  return {
    dontShowMessagePreview,
    setDontShowMessagePreview,
    profileDontShowGeneratedMessage,
    dontShowWhatsAppMessagePreview,
    setDontShowWhatsAppMessagePreview,
    profileDontShowWhatsappMessage,
    dontShowQuestionnairePreview,
    setDontShowQuestionnairePreview,
    profileDontShowQuestionnairePreview,
    updateFeatureToggles,
  };
};
